
import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  ScrollView,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { useAuth } from '@/contexts/AuthContext';
import PrimaryButton from '@/components/PrimaryButton';

export default function DeleteAccountConfirmationScreen() {
  const router = useRouter();
  const { signOut } = useAuth();

  // PRD §5.11 — `account_deleted` is fired in `app/delete-account.tsx`
  // success branch (the production flow goes straight from there to
  // `/welcome` and never re-mounts this confirmation screen, so a mount
  // log here would be unreachable). If this screen is ever re-introduced
  // into the success path, move the fire back here to avoid the
  // dual-canonical-site double-count.

  const handleUnderstood = async () => {
    try {
      // Sign out the user
      await signOut();
      // Navigate to welcome page
      router.replace('/welcome');
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if sign out fails, navigate to welcome
      router.replace('/welcome');
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView 
        style={styles.scrollView} 
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Success Icon */}
        <View style={styles.iconContainer}>
          <View style={styles.successIcon}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
          </View>
        </View>

        {/* Title */}
        <Text style={styles.title}>Solicitação de exclusão recebida</Text>

        {/* Message */}
        <View style={styles.messageContainer}>
          <Text style={styles.messageText}>
            Sua conta foi bloqueada e será excluída permanentemente em <Text style={styles.highlightText}>30 dias</Text>.
          </Text>

          <View style={styles.infoBox}>
            <View style={styles.infoItem}>
              <View style={styles.infoBullet} />
              <Text style={styles.infoText}>
                Você pode cancelar a exclusão fazendo login novamente durante os próximos 30 dias
              </Text>
            </View>

            <View style={styles.infoItem}>
              <View style={styles.infoBullet} />
              <Text style={styles.infoText}>
                Após este período, todos os seus dados serão removidos permanentemente
              </Text>
            </View>

            <View style={styles.infoItem}>
              <View style={styles.infoBullet} />
              <Text style={styles.infoText}>
                Você receberá lembretes por email antes da exclusão final
              </Text>
            </View>
          </View>

          <Text style={styles.footerText}>
            Se você mudou de ideia, entre novamente no aplicativo antes do prazo para cancelar a exclusão.
          </Text>
        </View>

        {/* Support Message */}
        <View style={styles.supportContainer}>
          <Text style={styles.supportTitle}>Precisa de ajuda?</Text>
          <Text style={styles.supportText}>
            Nossa equipe está sempre disponível para apoiá-lo em sua jornada de recuperação.
          </Text>
        </View>
      </ScrollView>

      {/* Bottom Button */}
      <View style={styles.buttonContainer}>
        <PrimaryButton
          title="Ok, entendido"
          onPress={handleUnderstood}
        />
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 24,
  },
  iconContainer: {
    alignItems: 'center',
    paddingTop: 80,
    paddingBottom: 32,
  },
  successIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#ECFDF5',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 28.8,
  },
  messageContainer: {
    marginBottom: 32,
  },
  messageText: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#475467',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  highlightText: {
    fontFamily: 'Inter_600SemiBold',
    color: '#151515',
  },
  infoBox: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: '#E7EAED',
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  infoBullet: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#10B981',
    marginTop: 6,
    marginRight: 12,
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#475467',
    lineHeight: 20,
  },
  footerText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 20,
  },
  supportContainer: {
    backgroundColor: '#FEF3F2',
    borderRadius: 12,
    padding: 20,
    marginBottom: 32,
    borderWidth: 1,
    borderColor: '#FECACA',
  },
  supportTitle: {
    fontSize: 16,
    fontFamily: 'Inter_600SemiBold',
    color: '#151515',
    marginBottom: 8,
    textAlign: 'center',
  },
  supportText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#475467',
    textAlign: 'center',
    lineHeight: 20,
  },
  buttonContainer: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    borderTopWidth: 1,
    borderTopColor: '#F0EEE4',
  },
  
});
